// Group provisioning: which of the organization's directory groups make
// their people operators. A mapped group says what its people may do, so
// they take a seat as they arrive; people in no mapped group wait for an
// administrator. Shown on the People page for organizations with the
// add-on.

import { Checkbox, Group, Paper, Select, Skeleton, Table, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { GroupMapping } from '@motionsafe/ms-console-api/admin/v1/mapping_pb.js';
import { OperatorRole, Product } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useId } from 'react';

import { api } from '../api/client';
import { errorText, useInvalidate } from '../api/hooks';
import { confirmFirst } from './confirm';
import { LOCKED_INPUT, LockedCaption } from './locked';
import { useAccess } from '../routes/_app';

const NOT_MAPPED = 'none';
const CHOICES = [
  { value: NOT_MAPPED, label: 'No seat' },
  { value: String(OperatorRole.UNRESTRICTED), label: 'A seat, unrestricted' },
  { value: String(OperatorRole.LAW_ENFORCEMENT), label: 'A seat, law enforcement' },
];
const productLabel = (p: Product) => (p === Product.INSIGHTS ? 'Insights' : 'Privacy');
const authority = (r: OperatorRole) => (r === OperatorRole.LAW_ENFORCEMENT ? 'law enforcement' : 'unrestricted');

interface Line { id: string; name: string; mapping: GroupMapping | null }

export function GroupProvisioning({ orgId, products }: { orgId: string; products: Product[] }) {
  const inv = useInvalidate();
  // An organization's administrators map their own groups; among staff, it is an admin's.
  const access = useAccess();
  const write = access.may('SetGroupMapping');
  const titleId = useId();
  const captionId = useId();
  const list = useQuery({ queryKey: ['mappings', orgId], queryFn: () => api.mapping.listGroupMappings({ orgId }) });
  const changed = (message: string) => { void inv('mappings', 'users', 'org', 'orgs'); notifications.show({ message }); };
  const fail = (e: unknown) => notifications.show({ color: 'danger', message: errorText(e) });
  const save = useMutation({
    mutationFn: (m: { line: Line; role: OperatorRole; restricted: Product[] }) => api.mapping.setGroupMapping({
      mapping: { id: m.line.mapping?.id ?? '', orgId, idpGroupId: m.line.id, idpGroupName: m.line.name, operatorRole: m.role, restrictedProducts: m.restricted, priority: m.line.mapping?.priority ?? 0 },
    }),
    onError: fail,
  });
  const remove = useMutation({ mutationFn: (mappingId: string) => api.mapping.deleteGroupMapping({ mappingId }), onError: fail });

  const lines: Line[] = [
    ...(list.data?.mappings ?? []).map((m) => ({ id: m.idpGroupId, name: m.idpGroupName || m.idpGroupId, mapping: m })),
    ...(list.data?.unmappedGroups ?? []).map((g) => ({ id: g.idpGroupId, name: g.idpGroupName || g.idpGroupId, mapping: null })),
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Giving a group seats, changing what it gives, and taking it away each say what follows before they happen.
  const choose = (line: Line, value: string | null) => {
    if (!value || !write) return;
    const now = line.mapping ? String(line.mapping.operatorRole) : NOT_MAPPED;
    if (value === now) return;
    if (value === NOT_MAPPED) {
      const mapping = line.mapping;
      if (!mapping) return;
      confirmFirst({
        title: `Stop assigning seats to ${line.name}?`,
        body: 'People who join this group from now on wait for a seat until you assign them one. People in it who hold a seat keep it, with the authority and products set on each of them by hand.',
        label: 'Stop assigning seats',
        onConfirm: () => remove.mutateAsync(mapping.id).then(() => changed(`${line.name} is no longer assigned seats.`)),
      });
      return;
    }
    const role = Number(value) as OperatorRole;
    confirmFirst({
      title: line.mapping ? `Make ${line.name} ${authority(role)}?` : `Assign seats to people in ${line.name}?`,
      body: line.mapping
        ? `Everyone in this group acts as ${authority(role)} from now on, whatever was set on them by hand.`
        : `Everyone in this directory group becomes an operator acting as ${authority(role)}, now and as they join it. Each takes a seat while one is free; after that they wait for one.`,
      label: line.mapping ? 'Change authority' : 'Assign seats',
      onConfirm: () => save.mutateAsync({ line, role, restricted: line.mapping?.restrictedProducts ?? [] }).then(() => changed(line.mapping ? `${line.name} is ${authority(role)}.` : `${line.name} is assigned seats.`)),
    });
  };
  const toggle = (line: Line, p: Product, allowed: boolean) => {
    const mapping = line.mapping;
    if (!mapping || !write) return;
    const restricted = allowed ? mapping.restrictedProducts.filter((x) => x !== p) : [...mapping.restrictedProducts, p];
    save.mutate({ line, role: mapping.operatorRole, restricted }, { onSuccess: () => changed(`${line.name} ${allowed ? 'can use' : 'cannot use'} ${productLabel(p)}.`) });
  };

  return (
    <section aria-labelledby={titleId} style={{ marginTop: 'var(--mantine-spacing-xl)' }}>
      <Title order={2} fz="lg" id={titleId}>Group provisioning</Title>
      <Text size="sm" c="dimmed" mt={4} mb="md" maw="72ch">
        People in a group that is assigned seats become operators without an invitation. People in your directory who are in none of them appear above, waiting for a seat, until you assign them one.
      </Text>
      <Paper p={0} style={{ overflow: 'hidden' }}>
        <Table.ScrollContainer minWidth={560}>
          <Table aria-labelledby={titleId} aria-busy={list.isPending}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Directory group</Table.Th>
                <Table.Th>Its people get</Table.Th>
                <Table.Th>Products</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.isPending && [0, 1, 2].map((i) => (
                <Table.Tr key={i} aria-hidden="true" bg="transparent">
                  {['40%', '60%', '50%'].map((w) => <Table.Td key={w}><Skeleton height={14} width={w} my={9} /></Table.Td>)}
                </Table.Tr>
              ))}
              {lines.map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td fw={500}>{line.name}</Table.Td>
                  <Table.Td>
                    <Select
                      aria-label={`What people in ${line.name} get`}
                      data={CHOICES}
                      value={line.mapping ? String(line.mapping.operatorRole || OperatorRole.UNRESTRICTED) : NOT_MAPPED}
                      onChange={(v) => choose(line, v)}
                      allowDeselect={false}
                      withCheckIcon={false}
                      w={240}
                      {...(write ? {} : { ...LOCKED_INPUT, 'aria-describedby': captionId })}
                    />
                  </Table.Td>
                  <Table.Td>
                    {line.mapping ? (
                      <Group gap="lg" wrap="nowrap">
                        {products.map((p) => (
                          <Checkbox
                            key={p}
                            label={productLabel(p)}
                            checked={!line.mapping!.restrictedProducts.includes(p)}
                            onChange={(e) => toggle(line, p, e.currentTarget.checked)}
                            {...(write ? { disabled: save.isPending } : { readOnly: true, 'aria-describedby': captionId })}
                          />
                        ))}
                      </Group>
                    ) : <Text size="sm" c="dimmed">None</Text>}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {list.isError && <Text size="sm" c="danger" ta="center" py="lg" px="lg">{errorText(list.error)}</Text>}
        {list.isSuccess && lines.length === 0 && (
          <Text size="sm" c="dimmed" ta="center" py="lg" px="lg">
            {list.data.directoryUnread
              ? 'Your directory\'s groups could not be read. Check that the directory is connected at your identity provider, then reload.'
              : 'Your directory has no groups yet. Groups appear here once the directory has some.'}
          </Text>
        )}
        {list.isSuccess && lines.length > 0 && list.data.directoryUnread && (
          <Text size="xs" c="dimmed" py="sm" px="lg">Your directory's groups could not be read, so only the groups that are assigned seats are listed.</Text>
        )}
      </Paper>
      {!write && <LockedCaption id={captionId}>{access.caption('SetGroupMapping')}</LockedCaption>}
    </section>
  );
}
